import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { AutorenewOutlined, ContentCopyOutlined, EmailOutlined, ForwardToInboxOutlined, InfoOutlined, NotificationsActiveOutlined } from '@mui/icons-material';
import moment from 'moment';
import { LoadingPage } from '../../loading-page';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import type { ISurveyInvitation, ISurveySupplierCoverage, SurveyInvitationStatus } from '../../../../shared/services/survey/survey.model';
import { AddRecipientsDialog } from '../../../dialogs/surveys/add-recipients-dialog';
import gaiaTheme from '../../../../root-components/theme';
import { SurveyHubTabProps } from '../survey-hub-page';

function invitationStatusChip(status?: SurveyInvitationStatus): { label: string; color: 'success' | 'warning' | 'default' } {
  switch (status) {
    case 'OPENED':
      return { label: 'In progress', color: 'warning' };
    case 'COMPLETED':
      return { label: 'Completed', color: 'success' };
    case 'PENDING':
    default:
      // Neutral: a recipient who has not started yet is the normal starting state,
      // not a failure. Error colouring is reserved for delivery problems.
      return { label: 'Not started', color: 'default' };
  }
}

/**
 * Collapses the delivery machinery (email queue states, legacy direct sends, schedule
 * states) into four readable groups. Every underlying state stays distinguishable
 * through the tooltip.
 */
function deliveryCell(inv: ISurveyInvitation): ReactNode {
  // The email queue is the authoritative source once an email has been queued.
  if (inv.emailQueueEntry) {
    const qs = inv.emailQueueEntry.status;
    if (qs === 'sent') {
      const ts = inv.sentAt || inv.emailQueueEntry.sentAt;
      return ts ? moment(ts).format('DD/MM/YYYY HH:mm') : 'Sent';
    }
    if (qs === 'failed') {
      return (
        <Tooltip title='The email could not be delivered. Send it again from Add recipients, or copy the invite link.'>
          <Chip label='Needs attention' size='small' color='error' variant='outlined' />
        </Tooltip>
      );
    }
    if (qs === 'cancelled') {
      return (
        <Tooltip title='The queued email was cancelled before sending.'>
          <Chip label='Not sent' size='small' color='default' variant='outlined' />
        </Tooltip>
      );
    }
    return (
      <Tooltip title='The email is queued and will be delivered shortly.'>
        <Chip label='Sending' size='small' color='info' variant='outlined' />
      </Tooltip>
    );
  }
  // Legacy: sentAt set without a queue entry (direct send path).
  if (inv.sentAt) {
    return moment(inv.sentAt).format('DD/MM/YYYY HH:mm');
  }
  // Not yet sent: show the schedule state.
  if (inv.scheduleId && inv.schedule) {
    if (!inv.schedule.active) {
      if (inv.schedule.scheduleType === 'one_off') {
        return (
          <Tooltip title='The one-off schedule has already run but this invitation was not sent. Send it manually via Add recipients.'>
            <Chip label='Needs attention' size='small' color='error' variant='outlined' />
          </Tooltip>
        );
      }
      return (
        <Tooltip title='The attached schedule is paused. Resume the schedule to send this invitation.'>
          <Chip label='Needs attention' size='small' color='warning' variant='outlined' />
        </Tooltip>
      );
    }
    const fireAt = inv.schedule.scheduleType === 'one_off'
      ? inv.schedule.runAt
      : inv.schedule.nextRunAt;
    const tooltipLabel = fireAt
      ? `Scheduled: ${moment(fireAt).format('DD/MM/YYYY HH:mm')}`
      : `Scheduled: ${inv.schedule.name}`;
    return (
      <Tooltip title={tooltipLabel}>
        <Chip label='Scheduled' size='small' color='info' variant='outlined' />
      </Tooltip>
    );
  }
  return (
    <Tooltip title='No email has been sent for this invitation.'>
      <Chip label='Not sent' size='small' color='default' variant='outlined' />
    </Tooltip>
  );
}

const headCells: IHeadCell[] = [
  { id: 'email', label: 'Email', numeric: false, sortable: false, includeInMobile: true },
  { id: 'name', label: 'Name', numeric: false, sortable: false },
  { id: 'supplier', label: 'Supplier', numeric: false, sortable: false },
  { id: 'status', label: 'Status', numeric: false, sortable: false, includeInMobile: true },
  { id: 'link', label: 'Invite Link', numeric: false, sortable: false },
  { id: 'delivery', label: 'Delivery', numeric: false, sortable: false },
  { id: 'actions', label: '', numeric: false, sortable: false, includeInMobile: true },
];

export const SurveyRecipientsTab = ({ survey, reload }: SurveyHubTabProps) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

  const [invitations, setInvitations] = useState<ISurveyInvitation[]>();
  const [totalRecords, setTotalRecords] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [selected, setSelected] = useState<string[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [coverage, setCoverage] = useState<ISurveySupplierCoverage | null>(null);
  const [isReminding, setIsReminding] = useState(false);
  // Client-side view filter: show only recipients who have not completed the survey.
  const [nonRespondersOnly, setNonRespondersOnly] = useState(false);

  const surveyId = survey.id;

  // Every invitation on the survey, for the non-responder filter and the reminder count.
  const fetchAllInvitations = useCallback(async (): Promise<ISurveyInvitation[]> => {
    let result = await surveyAdminService.getInvitations(surveyId, { page: 0, perPage: 200 });
    if (result.rows.length < result.count) {
      result = await surveyAdminService.getInvitations(surveyId, { page: 0, perPage: result.count });
    }
    return result.rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // With the filter on, `invitations` holds every non-responder (paged client-side);
  // otherwise it holds the current server page.
  const fetchInvitations = useCallback(async () => {
    if (nonRespondersOnly) {
      const all = await fetchAllInvitations();
      const filtered = all.filter(inv => inv.status !== 'COMPLETED');
      setInvitations(filtered);
      setTotalRecords(filtered.length);
      return;
    }
    const result = await surveyAdminService.getInvitations(surveyId, { page: inputPage, perPage });
    setInvitations(result.rows);
    setTotalRecords(result.count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId, inputPage, perPage, nonRespondersOnly, fetchAllInvitations]);

  const loadInvitations = useCallback(async () => {
    setInvitations(undefined);
    try {
      await fetchInvitations();
    } catch {
      setInvitations([]);
    }
  }, [fetchInvitations]);

  // Silent refresh (no loading spinner) used for polling and after row actions.
  const silentRefresh = useCallback(async () => {
    try {
      await fetchInvitations();
    } catch {
      // ignore polling errors
    }
  }, [fetchInvitations]);

  const loadCoverage = useCallback(async () => {
    try {
      setCoverage(await surveyAdminService.getSupplierCoverage(surveyId));
    } catch {
      setCoverage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  useEffect(() => { loadInvitations(); loadCoverage(); }, [loadInvitations, loadCoverage]);

  // Poll every 5 seconds while any invitation is queued but not yet delivered. Only runs
  // while this tab is mounted.
  useEffect(() => {
    if (!invitations) return;
    const hasPending = invitations.some(
      inv => (inv.emailQueueId || inv.emailQueueEntry) && !inv.sentAt
        && inv.emailQueueEntry?.status !== 'sent'
        && inv.emailQueueEntry?.status !== 'failed'
        && inv.emailQueueEntry?.status !== 'cancelled',
    );
    const hasScheduledPending = invitations.some(inv => inv.scheduleId && !inv.sentAt);
    if (!hasPending && !hasScheduledPending) return;
    const timer = setInterval(silentRefresh, 5000);
    return () => clearInterval(timer);
  }, [invitations, silentRefresh]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      userNotificationsContext.openSnackbar({ open: true, message: 'Copied to clipboard', backgroundColor: 'primary.dark' });
    });
  };

  // The backend reminds invitations that have had their email sent and are not
  // completed; resend goes through the same route targeted at one invitation.
  const canResend = (inv: ISurveyInvitation) => Boolean(inv.sentAt) && inv.status !== 'COMPLETED';

  const resendHandler = async (inv: ISurveyInvitation) => {
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Resend invitation email',
      message: `Send the invitation email again to ${inv.email}?`,
      buttonCopy: 'Resend email',
      cancelButtonCopy: 'Cancel',
    }).catch(() => false);
    if (!confirmed) return;
    try {
      const result = await surveyAdminService.remindInvitations(surveyId, { invitationIds: [inv.id] });
      userNotificationsContext.openSnackbar({
        open: true,
        message: result.reminded > 0
          ? `Invitation email queued for ${inv.email}`
          : 'This invitation could not be resent. Only invitations whose email has been sent, and which are not completed, can be resent.',
        backgroundColor: result.reminded > 0 ? 'primary.dark' : 'text.primary',
      });
      silentRefresh();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to resend the invitation email', backgroundColor: 'text.primary' });
    }
  };

  const regenerateHandler = async (inv: ISurveyInvitation) => {
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Regenerate invite link',
      message: `Create a new invite link for ${inv.email}? The current link stops working immediately, so anyone holding it can no longer respond with it.`,
      buttonCopy: 'Regenerate link',
      cancelButtonCopy: 'Cancel',
    }).catch(() => false);
    if (!confirmed) return;
    let inviteToken: string;
    try {
      ({ inviteToken } = await surveyAdminService.regenerateInviteToken(surveyId, inv.id));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to regenerate the invite link';
      userNotificationsContext.openSnackbar({ open: true, message: msg, backgroundColor: 'text.primary' });
      return;
    }
    // From here the regenerate HAS succeeded: refresh so the row carries the new
    // link whatever the clipboard does. The clipboard write can be refused (user
    // activation has expired after two awaits, notably on Safari), so it gets its
    // own try/catch and a success message either way.
    silentRefresh();
    const newUrl = `${window.location.origin}/survey-respond?inviteToken=${inviteToken}`;
    try {
      await navigator.clipboard.writeText(newUrl);
      userNotificationsContext.openSnackbar({
        open: true,
        message: `New invite link for ${inv.email} copied to clipboard. The old link no longer works.`,
        backgroundColor: 'primary.dark',
      });
    } catch {
      userNotificationsContext.openSnackbar({
        open: true,
        message: 'Invite link regenerated. Copy the new link from the row; the old link no longer works.',
        backgroundColor: 'primary.dark',
      });
    }
  };

  // With the non-responder filter on, `invitations` holds every match, so page client-side.
  const visibleInvitations = nonRespondersOnly
    ? (invitations ?? []).slice(inputPage * perPage, (inputPage + 1) * perPage)
    : (invitations ?? []);

  const rows = visibleInvitations.map(inv => {
    const inviteUrl = `${window.location.origin}/survey-respond?inviteToken=${inv.inviteToken}`;
    const statusChip = invitationStatusChip(inv.status);
    return {
      id: inv.id,
      email: inv.email,
      name: inv.recipientName ?? '—',
      supplier: inv.contactSupplier?.name
        ? <Chip label={inv.contactSupplier.name} size='small' variant='outlined' />
        : '—',
      status: (
        <Chip label={statusChip.label} color={statusChip.color} size='small' variant='filled' />
      ),
      link: (
        <Tooltip title={inviteUrl}>
          <IconButton size='small' onClick={() => copyToClipboard(inviteUrl)}>
            <ContentCopyOutlined fontSize='small' />
          </IconButton>
        </Tooltip>
      ),
      delivery: deliveryCell(inv),
      actions: (
        <TableActionButtons
          id={inv.id}
          additionalMenuItems={[
            {
              label: 'Resend invitation email',
              onClick: () => resendHandler(inv),
              disabled: !canResend(inv),
              icon: <ForwardToInboxOutlined sx={{ mr: 1 }} />,
            },
            {
              label: 'Regenerate invite link',
              onClick: () => regenerateHandler(inv),
              disabled: inv.status === 'COMPLETED',
              icon: <AutorenewOutlined sx={{ mr: 1 }} />,
            },
          ]}
        />
      ),
    };
  });

  const remindHandler = async () => {
    setIsReminding(true);
    try {
      // Fetch every invitation so the confirmation carries the real count. The backend
      // reminds invitations that are not COMPLETED and have had their email sent, so
      // count with the same predicate.
      const allRows = await fetchAllInvitations();
      const notResponded = allRows.filter(inv => inv.status !== 'COMPLETED');
      const remindable = notResponded.filter(inv => Boolean(inv.sentAt)).length;
      if (remindable === 0) {
        userNotificationsContext.openSnackbar({
          open: true,
          message: notResponded.length === 0
            ? 'Everyone has already completed the survey: there are no reminders to send'
            : 'No reminders to send: reminder emails go to recipients whose invitation email has already been sent',
          backgroundColor: 'primary.dark',
        });
        return;
      }
      const confirmed = await userNotificationsContext.openDialog({
        active: true,
        title: 'Send reminders',
        message: remindable === 1
          ? 'Email a reminder to 1 person who has not responded?'
          : `Email a reminder to ${remindable} people who have not responded?`,
        buttonCopy: 'Send reminders',
        cancelButtonCopy: 'Cancel',
      }).catch(() => false);
      if (!confirmed) return;
      const result = await surveyAdminService.remindInvitations(surveyId);
      userNotificationsContext.openSnackbar({
        open: true,
        message: result.reminded === 0
          ? 'Everyone has already completed the survey: there are no reminders to send'
          : `Reminder email${result.reminded === 1 ? '' : 's'} queued for ${result.reminded} recipient${result.reminded === 1 ? '' : 's'}`,
        backgroundColor: 'primary.dark',
      });
      silentRefresh();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'An error occurred, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsReminding(false);
    }
  };

  // At least one invitation on this page has not responded, or other pages exist whose
  // status we cannot see from here. Reminders are survey-scoped, so the button lives in
  // the toolbar rather than inside the supplier-coverage summary.
  const mayHaveNonResponders = totalRecords > 0 && (
    (invitations ?? []).some(inv => inv.status !== 'COMPLETED')
    || totalRecords > (invitations?.length ?? 0)
  );

  return (
    <Box>
      <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mb: 2 }}>
        {mayHaveNonResponders && (
          <Button
            variant='outlined'
            startIcon={<NotificationsActiveOutlined />}
            onClick={remindHandler}
            disabled={isReminding}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Email reminders to non-responders
          </Button>
        )}
        <Button
          variant='contained'
          startIcon={<EmailOutlined />}
          onClick={() => setOpenDialog(true)}
        >
          Add recipients
        </Button>
      </Stack>
      {coverage && coverage.invitedSuppliers > 0 && (
        <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
          <Stack spacing={0.5}>
            <Stack direction='row' spacing={0.75} alignItems='center'>
              <Typography variant='subtitle2'>Supplier coverage</Typography>
              <Tooltip
                title={'The percentage is the share of this reporting period\'s supplier emissions attributable to invited suppliers who have responded, weighted by emissions. It is not a count of replies: one large supplier moves it more than several small ones.'}
              >
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
            </Stack>
            <Typography variant='body2' color='text.secondary'>
              {coverage.respondedSuppliers} of {coverage.invitedSuppliers} invited supplier{coverage.invitedSuppliers === 1 ? '' : 's'} responded
              {coverage.respondedTco2ePct !== null && coverage.respondedTco2ePct !== undefined
                ? `, covering ${coverage.respondedTco2ePct}% of this period's supplier emissions`
                : ''}
            </Typography>
            {!nonRespondersOnly && coverage.respondedSuppliers < coverage.invitedSuppliers && (
              <Box>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => { setNonRespondersOnly(true); setInputPage(0); }}
                  sx={{ px: 0.5, minWidth: 0, textTransform: 'none' }}
                >
                  See who hasn't responded
                </Button>
              </Box>
            )}
          </Stack>
        </Paper>
      )}
      {nonRespondersOnly && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label='Showing only recipients who have not responded'
            color='primary'
            variant='outlined'
            onDelete={() => { setNonRespondersOnly(false); setInputPage(0); }}
          />
        </Box>
      )}
      <LoadingPage pageReady={!!invitations}>
        <TableDisplay
          rows={rows}
          headCells={headCells}
          isMobileMode={isMobileMode}
          selected={selected}
          setSelected={setSelected}
          totalRecords={totalRecords}
          inputPage={inputPage}
          setInputPage={setInputPage}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>
      <AddRecipientsDialog
        supplierOnly={survey.purpose === 'SUPPLIER_PRODUCT_DATA'}
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        successHandler={() => { loadInvitations(); loadCoverage(); }}
        surveyId={surveyId}
        surveyStatus={survey.status}
        onActivated={reload}
      />
    </Box>
  );
};
